/**
 * Cálculo de los totales de un documento de venta.
 *
 * Separado del componente a propósito: **aquí está el dinero**, y el
 * dinero se prueba sin React.
 *
 * Lo que hace especial a este módulo no se ve: los totales los calcula
 * `computeDocumentTotals` del dominio, la misma función que usa el
 * servidor al guardar.
 *
 * No es purismo. El IVA de un documento se calcula sobre la **base
 * acumulada por tipo**, nunca sumando el IVA de cada línea: con veinte
 * líneas al 21 %, las dos formas dan resultados distintos por redondeo, y
 * la diferencia acaba en un registro Veri\*Factu que ya no se puede
 * cambiar. Si la pantalla sumara por su cuenta, enseñaría un total y el
 * servidor guardaría otro.
 *
 * Por eso el dinero nunca pasa por `number`: entra como cadena decimal, se
 * convierte a `Money` y vuelve a salir como cadena.
 */

import { useMemo } from 'react';
import { Money, computeDocumentTotals, type SalesLineInput } from '@fibwifire/domain/browser';

export type LineKind = 'PRODUCTO' | 'SERVICIO' | 'MANO_OBRA' | 'DESPLAZAMIENTO' | 'TEXTO';

export interface EditableLine {
  /** Identificador local, sólo para React y para el cálculo. */
  lineId: string;
  kind: LineKind;
  description: string;
  quantity: string;
  unitPrice: string;
  discountRate: string;
  taxRate: string;
}

export const KINDS: ReadonlyArray<{ value: LineKind; label: string }> = [
  { value: 'PRODUCTO', label: 'Producto' },
  { value: 'SERVICIO', label: 'Servicio' },
  { value: 'MANO_OBRA', label: 'Mano de obra' },
  { value: 'DESPLAZAMIENTO', label: 'Desplazamiento' },
  { value: 'TEXTO', label: 'Texto' },
];

/** Tipos vigentes en España. El 0 % exige mención de la causa de exención. */
export const TAX_RATES = ['21', '10', '4', '0'] as const;

export function emptyLine(lineId: string): EditableLine {
  return {
    lineId,
    kind: 'SERVICIO',
    description: '',
    quantity: '1',
    unitPrice: '0.00',
    discountRate: '',
    taxRate: '21',
  };
}

/** ¿Es un decimal que se puede convertir sin sorpresas? */
function isDecimal(value: string): boolean {
  return /^-?\d+([.,]\d+)?$/.test(value.trim());
}

function normalize(value: string): string {
  // Se acepta la coma decimal porque es como se teclea en España, y se
  // normaliza al punto, que es lo que la API espera.
  return value.trim().replace(',', '.');
}

export interface LineTotals {
  gross: string;
  discount: string;
  taxableBase: string;
  taxTotal: string;
  total: string;
  breakdown: Array<{ taxRate: string; taxableBase: string; taxAmount: string }>;
  perLine: Record<string, string>;
  /** Líneas cuyos números no se pueden interpretar todavía. */
  invalidLines: string[];
}

/**
 * Totales del documento.
 *
 * Se recalculan en cada tecleo, pero sólo con las líneas cuyos números son
 * interpretables: mientras alguien escribe «12,», esa línea no cuenta en
 * lugar de hacer saltar el total a cero.
 */
export function computeLineTotals(
  lines: readonly EditableLine[],
  globalDiscountRate: string,
  currency = 'EUR',
): LineTotals {
  {
    const invalidLines: string[] = [];
    const inputs: SalesLineInput[] = [];

    for (const line of lines) {
      if (line.kind === 'TEXTO') continue;

      const quantity = normalize(line.quantity);
      const unitPrice = normalize(line.unitPrice);

      if (!isDecimal(quantity) || !isDecimal(unitPrice)) {
        invalidLines.push(line.lineId);
        continue;
      }

      try {
        inputs.push({
          lineId: line.lineId,
          kind: line.kind,
          description: line.description,
          quantity,
          unitPrice: Money.of(unitPrice, currency),
          discountRate: line.discountRate.trim() === '' ? null : normalize(line.discountRate),
          taxRate: line.taxRate,
        });
      } catch {
        invalidLines.push(line.lineId);
      }
    }

    const empty: LineTotals = {
      gross: '0.00',
      discount: '0.00',
      taxableBase: '0.00',
      taxTotal: '0.00',
      total: '0.00',
      breakdown: [],
      perLine: {},
      invalidLines,
    };

    if (inputs.length === 0) return empty;

    try {
      const totals = computeDocumentTotals(inputs, {
        currency,
        globalDiscountRate:
          globalDiscountRate.trim() === '' ? null : normalize(globalDiscountRate),
      });

      const perLine: Record<string, string> = {};
      for (const line of totals.lines) perLine[line.lineId] = line.net.toString();

      return {
        gross: totals.gross.toString(),
        discount: totals.globalDiscount.toString(),
        taxableBase: totals.taxableBase.toString(),
        taxTotal: totals.taxTotal.toString(),
        total: totals.total.toString(),
        breakdown: totals.taxBreakdown.map((entry) => ({
          taxRate: entry.taxRate,
          taxableBase: entry.taxableBase.toString(),
          taxAmount: entry.taxAmount.toString(),
        })),
        perLine,
        invalidLines,
      };
    } catch {
      // Un descuento global imposible —«200 %»— no debe romper la
      // pantalla: se enseña el documento sin totales y el campo marcado.
      return empty;
    }
  }
}

/** El mismo cálculo, memorizado para el componente. */
export function useLineTotals(
  lines: readonly EditableLine[],
  globalDiscountRate: string,
  currency = 'EUR',
): LineTotals {
  return useMemo(
    () => computeLineTotals(lines, globalDiscountRate, currency),
    [lines, globalDiscountRate, currency],
  );
}

